import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Link, useParams } from '@reach/router';
import colors from '../../../themes/main-colors';
import PGEButton from '../../buttons';
import Alerts from '../../alerts/Alerts';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { useInstallerJobDetailsQuery } from '../../../hooks/pge-plus/useInstallerJobDetailsQuery';
import {
  DEFAULT_MIME_TYPES,
  FileUploader,
  MIME_TYPE_PDF,
} from '../../pge-plus-common/FileUploader';
import useQuote from './useQuote';
import InstallerPageHeader from '../../pge-plus-common/InstallerPageHeader';
import InstallerDateAndTime from '../installer-common/InstallerDateAndTime';
import {
  subText1,
  boldText,
  mainContactFields,
} from '../installer-common/quoteCss';
import QuoteCustomerInfo from '../installer-common/quote-customer-info';
import JobInstallationCost from '../installer-common/jobInstallationCost';
import QuoteTotalSection from '../installer-common/quoteTotalSection';
import { JobQuoteModal } from './QuoteForm.rules';
import {
  ApplicationEv,
  LineItemCost,
  LineItemDetail,
  LineItemProduct,
  QuoteDataEv,
  OcrFieldNames,
  SectionEvRebates,
  ValidateSerialNumberInput,
} from '../../../__generated__/pgeplus-types';
import Backdrop from '../../backdrop';
import { FormState } from '../../../hooks/useFormState.types';
import useUtil from '../../../hooks/pge-plus/useUtil';
import { LINE_ITEM_KEY } from '../quote.types';
import routes from '../../../routes';
import FileUploaderWithOCR from '../../pge-plus-common/FileUploaderWithOCR';
import useValidateSerialNumber from '../../../hooks/pge-plus/useValidateSerialNumber';

function getDefaultJobValues(
  rebateSection: SectionEvRebates,
  lineItemDetailList: LineItemDetail[],
  lineItemCostList: LineItemCost[],
  lineItemProductList: LineItemProduct[],
  jobTypeValue: string,
  chargerModalValue: string,
  content: PageTextContentType,
  defaultFormState: FormState<JobQuoteModal>,
): JobQuoteModal {
  return {
    ...defaultFormState.values,
    isCreateQuote: false,
    jobType: jobTypeValue,
    totalInstallationMaterialCost:
      (lineItemCostList &&
        lineItemCostList.filter(
          item => item.key === LINE_ITEM_KEY.INSTALLATION_MATERIALS,
        )?.[0]?.amount) ||
      0,
    meterBase:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.METER_BASE)?.[0]
        ?.amount.toFixed(2),
    weatherHead:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.WEATHER_HEAD)?.[0]
        ?.amount.toFixed(2),
    electricBreaker:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.ELECTRICAL_BREAKER)?.[0]
        ?.amount.toFixed(2),
    miscMaterial:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MISC_MATERIALS)?.[0]
        ?.amount.toFixed(2),
    labor:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.LABOR)?.[0]
        ?.amount.toFixed(2),
    material:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MATERIALS)?.[0]
        ?.amount?.toFixed(2),
    permitCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.PERMITS)?.[0]
        .amount.toFixed(2),
    travelCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.TRAVEL_TIME)?.[0]
        ?.amount?.toFixed(2),
    feesCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.FEES)?.[0]
        ?.amount?.toFixed(2),
    miscellaneousWorkCost:
      lineItemCostList &&
      lineItemCostList
        .filter(item => item.key === LINE_ITEM_KEY.MISCELLANEOUS_WORK)?.[0]
        ?.amount?.toFixed(2),
    additionalComment:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.COMMENTS,
      )?.[0]?.value,
    additionalNotesToCustomer:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.CUSTOMER_VISIBLE_NOTES,
      )?.[0]?.value,
    chargerCost:
      lineItemProductList &&
      lineItemProductList
        ?.filter(item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL)?.[0]
        ?.amount?.toFixed(2),
    amperage:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_AMPERAGE,
      )?.[0]?.value,
    isReplace:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_UPGRADE,
      )?.[0]?.value,
    isDistance:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item =>
          item.key === LINE_ITEM_KEY.CHARGER_INSTALL_WITHIN_PANEL_DISTANCE,
      )?.[0]?.value,
    cardinalDirections:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION_CARDINAL_DIRECTION,
      )?.[0]?.value,
    outsidePanelLocation:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION,
      )?.[0]?.value,
    distance:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PREFERRED_INSTALL_DISTANCE,
      )?.[0]?.value,
    panelLocation:
      lineItemDetailList &&
      lineItemDetailList.filter(
        item => item.key === LINE_ITEM_KEY.PANEL_LOCATION_MAIN_BREAKER,
      )?.[0]?.value,
    rebateEligibility:
      rebateSection && rebateSection?.data?.rebateEligibility?.eligible
        ? content.get('REUSABLE_YES')
        : content.get('REUSABLE_NO'),
    chargerModel: chargerModalValue,
    adminFee:
      (lineItemCostList &&
        lineItemCostList.filter(
          item => item.key === LINE_ITEM_KEY.ADMIN_FEE,
        )?.[0]?.amount) ||
      undefined,
  };
}

const ConfirmDetailsAndCost = () => {
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_CONFIRMATION_DETAILS_AND_COST,
  );
  const ocrText = useRef<PageTextContentType | undefined>();
  ocrText.current = content;
  const {
    totalInstallationMaterialCost,
    totalInstallation,
    totalCost,
    costBeforeRebate,
    setTotalInstallationMaterialCost,
    isUpdate,
    rebateAmount,
    panelRebateAmount,
    jobQuoteForm,
    resetExpandFormData,
    calculateTotalAmount,
    submitJob,
    rebateCalculate,
    chargerModelList,
    setChargerModelList,
    isCalculatingRebates,
  } = useQuote(content);
  const { validateSerialNumber, isValidating } = useValidateSerialNumber();

  const { setErrorNotification } = useUtil();

  const params = useParams();
  const [isPanelUpgrade, setIsPanelUpgrade] = useState<boolean>();
  const { loading, data } = useInstallerJobDetailsQuery(params?.jobId || '');
  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const jobData = data?.getJob.job;
  const applicationData = data?.getJob.application as ApplicationEv;
  const theme = useTheme();
  const [ocrExtractionStatus, setOcrExtractionStatus] = useState<
    string | undefined
  >();
  const [failCount, setFailCount] = useState<number>(0);
  const [warnedInput, setWarnedInput] = useState<ValidateSerialNumberInput>();
  const modalIdNumberProps = jobQuoteForm.props('modalIdNumber');
  const showDuplicateSerialWarning = Boolean(
    warnedInput &&
      warnedInput.applicationNo === jobData?.applicationNo &&
      warnedInput.serialNumber === jobQuoteForm.values.modalIdNumber,
  );

  useEffect(() => {
    if (data?.getJob) {
      const chargerModalValue =
        data?.getJob.job.quote &&
        (data?.getJob.job.quote?.data?.lineItems?.filter(
          item => item.key === 'EV_CHARGER_MODEL',
        )[0] as LineItemProduct).value;
      const chargerModalName =
        data?.getJob.job.quote &&
        (data?.getJob.job.quote?.data?.lineItems?.filter(
          item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
        )[0] as LineItemProduct).name;

      const jobTypeValue =
        data?.getJob.job.quote &&
        (data?.getJob.job.quote.data.lineItems?.filter(
          (item: any) => item.key === LINE_ITEM_KEY.JOB_TYPE,
        )[0] as any).value;
      const rebateSection = data?.getJob.application.details?.filter(
        detail => detail.sectionType === 'EV_REBATES',
      )[0] as SectionEvRebates;
      const lineItemDetailList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemDetail',
      ) as LineItemDetail[];
      const lineItemCostList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemCost',
      ) as LineItemCost[];
      const lineItemProductList = data?.getJob.job.quote?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemProduct',
      ) as LineItemProduct[];
      if (!chargerModalName.toLowerCase().includes('juicebox')) {
        setShowSerialNumber(true);
      } else {
        setShowSerialNumber(false);
      }
      jobQuoteForm.reset(
        getDefaultJobValues(
          rebateSection,
          lineItemDetailList,
          lineItemCostList,
          lineItemProductList,
          jobTypeValue,
          chargerModalValue,
          content,
          jobQuoteForm,
        ),
      );
      calculateTotalAmount(false, {
        totalInstallationMaterial:
          (lineItemCostList &&
            lineItemCostList.filter(
              item => item.key === LINE_ITEM_KEY.INSTALLATION_MATERIALS,
            )[0].amount) ||
          0,
        chargerCost:
          lineItemProductList &&
          lineItemProductList?.filter(
            item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
          )[0].amount,
        meterBase:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.METER_BASE,
          )[0].amount,
        weatherHead:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.WEATHER_HEAD,
          )[0].amount,
        electricBreaker:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.ELECTRICAL_BREAKER,
          )[0].amount,
        miscMaterial:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MISC_MATERIALS,
          )[0].amount,
        labor:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.LABOR)[0]
            .amount,
        material:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MATERIALS,
          )[0].amount,
        permitCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.PERMITS)[0]
            .amount,
        travelCost:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.TRAVEL_TIME,
          )[0].amount,
        feesCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === LINE_ITEM_KEY.FEES)[0]
            .amount,
        miscellaneousWorkCost:
          lineItemCostList &&
          lineItemCostList.filter(
            item => item.key === LINE_ITEM_KEY.MISCELLANEOUS_WORK,
          )[0].amount,
      });
    }
  }, [jobData]);

  useEffect(() => {
    setWarnedInput(undefined);
  }, [jobData?.applicationNo]);

  useEffect(() => {
    if (!(jobData?.quote?.data as QuoteDataEv)?.declineEnrollment) {
      void rebateCalculate(applicationData);
    }
  }, [
    applicationData,
    isPanelUpgrade,
    isUpdate,
    jobData,
    jobQuoteForm.values.amperage,
  ]);

  const button = {
    background: colors.orange,
    width: '200px',
    borderRadius: '30px',
    fontSize: '18px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '&:hover': {
      backgroundColor: colors.orange,
    },
  };

  const hr = {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  };
  return (
    <Fragment>
      {loading && <Backdrop forceOpen />}
      <Box
        display="flex"
        flexDirection="column"
        mt={1}
        css={{
          maxWidth: '706px',
          justifyContent: 'center',
          margin: '0 auto',
          marginTop: 16,
          marginBottom: 100,
          '& .MuiFormControl-root': {
            background: 'transparent',
          },
          '& .MuiInputBase-input': {
            background: '#FFF',
          },
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <InstallerPageHeader
              pageHeaderInfo={{
                pageTitle:
                  content.get('CONFIRMATION_DETAILS_AND_COST_HEADER') || '',
                pageSubTitle: content.get('CONFIRMATION_DETAILS_AND_COST_INFO'),
              }}
              jobData={data?.getJob!}
              content={content}
            />
            <InstallerDateAndTime
              installationDateTime={jobData?.serviceTime}
              jobQuoteForm={jobQuoteForm}
              editDateAndTime={false}
              parentPage={'confirm-quote'}
              content={content}
              title={content.get('REUSABLE_INSTALLATION_DATE_AND_TIME')}
            />
            <hr css={{ ...hr }} />
            <QuoteCustomerInfo
              content={content}
              jobData={jobData?.jobLocation!}
              applicationNo={applicationData?.applicationNo}
            />
            <hr css={{ ...hr }} />

            <JobInstallationCost
              content={content}
              applicationData={applicationData}
              title={content.get('CONFIRMATION_DETAILS_AND_COST_INSTRUCTIONS')}
              resetExpandFormData={resetExpandFormData}
              calculateTotalAmount={calculateTotalAmount}
              jobQuoteForm={jobQuoteForm}
              setIsPanelUpgrade={setIsPanelUpgrade}
              panelRebateAmount={panelRebateAmount}
              totalInstallationMaterialCost={totalInstallationMaterialCost}
              totalInstallationCost={totalInstallation}
              setShowSerialNumber={setShowSerialNumber}
              chargerModelList={chargerModelList}
              setChargerModelList={setChargerModelList}
            />
            <hr css={{ ...hr }} />
            <Grid>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...subText1 }}>
                  {content.get('PHOTOS_TO_BE_UPLOADED')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {showSerialNumber
                    ? content.get('CHARGER_SERIAL_NUMBER')
                    : content.get('JUICE_BOX_ID_NUMBER')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <FileUploaderWithOCR
                  placeholder={content.get('REUSABLE_UPLOAD_AND_SCAN_PHOTO')}
                  reuploadLabel={content.get('REUPLOAD_PHOTO')!}
                  ocrType={
                    showSerialNumber
                      ? OcrFieldNames.SerialNumber
                      : OcrFieldNames.JuiceboxId
                  }
                  onComplete={(key, extractedText, message, status) => {
                    setWarnedInput(undefined);
                    void jobQuoteForm.setValue('chargerPhoto', key);
                    void jobQuoteForm.setValue('modalIdNumber', extractedText);
                    setOcrExtractionStatus(status);
                    if (status === 'fail') {
                      setFailCount(count => count + 1);
                    } else {
                      setFailCount(0);
                    }
                  }}
                  image={{ id: jobQuoteForm.values?.chargerPhoto || '' }}
                  acceptMimeTypes={{
                    ...DEFAULT_MIME_TYPES,
                    ...MIME_TYPE_PDF,
                  }}
                />

                {jobQuoteForm.errors.chargerPhoto && (
                  <FormHelperText error>
                    {jobQuoteForm.errors.chargerPhoto}
                  </FormHelperText>
                )}

                <FormHelperText error>
                  {ocrExtractionStatus && ocrExtractionStatus === 'fail' && (
                    <FormHelperText error component="div">
                      {failCount > 1
                        ? content.get('OCR_PROCESS_REUPLOAD_MESSAGE')
                        : content.get('OCR_PROCESS_FAIL_MESSAGE')}
                    </FormHelperText>
                  )}
                </FormHelperText>
              </Box>
              {jobQuoteForm.values.chargerPhoto && (
                <Box css={{ marginBottom: theme.spacing(3) }}>
                  <TextField
                    css={{ ...mainContactFields }}
                    name={'name'}
                    label={content.get('jobQuoteForm')}
                    {...modalIdNumberProps}
                    onChange={async event => {
                      setWarnedInput(undefined);
                      await modalIdNumberProps.onChange(event);
                    }}
                    variant={'outlined'}
                  />

                  {ocrExtractionStatus && ocrExtractionStatus === 'success' && (
                    <Typography css={{ marginTop: 8 }}>
                      {content.get('OCR_PROCESS_COMPLETION_MESSAGE')}
                    </Typography>
                  )}
                </Box>
              )}
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {content.get('COMPLETED_CHARGER_INSTALLATION')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <FileUploader
                  image={jobQuoteForm.values.installedChargerPhoto}
                  placeholder={content.get('UPLOAD_A_PHOTO')}
                  reuploadLabel={content.get('REUPLOAD_PHOTO')!}
                  onUpload={key =>
                    jobQuoteForm.setValue('installedChargerPhoto', key)
                  }
                  acceptMimeTypes={{
                    ...DEFAULT_MIME_TYPES,
                    ...MIME_TYPE_PDF,
                  }}
                />

                {jobQuoteForm.errors.installedChargerPhoto && (
                  <FormHelperText error>
                    {jobQuoteForm.errors.installedChargerPhoto}
                  </FormHelperText>
                )}
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {content.get('COPY_OF_CUSTOMERS_INVOICE')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <FileUploader
                  image={jobQuoteForm.values.customerInvoicePhoto}
                  placeholder={content.get('UPLOAD_A_PHOTO')}
                  reuploadLabel={content.get('REUPLOAD_PHOTO')!}
                  onUpload={key =>
                    jobQuoteForm.setValue('customerInvoicePhoto', key)
                  }
                  acceptMimeTypes={{
                    ...DEFAULT_MIME_TYPES,
                    ...MIME_TYPE_PDF,
                  }}
                />

                {jobQuoteForm.errors.customerInvoicePhoto && (
                  <FormHelperText error>
                    {jobQuoteForm.errors.customerInvoicePhoto}
                  </FormHelperText>
                )}
              </Box>
            </Grid>

            <QuoteTotalSection
              content={content}
              totalCost={totalCost}
              costBeforeRebate={costBeforeRebate}
              rebateAmount={rebateAmount}
              isCalculatingRebates={isCalculatingRebates}
            />
            <hr css={{ ...hr }} />
            <Grid>
              <Typography
                css={{
                  fontSize: '14px',
                  color: '#3D5265',
                  marginBottom: theme.spacing(2),
                }}
              >
                {content.get('LEGAL_TEXT')}
              </Typography>
            </Grid>
          </Grid>
          {showDuplicateSerialWarning && (
            <Grid item xs={12}>
              <Alerts
                isOpen={true}
                severity={'warning'}
                variant={'filled'}
                message={content.get('DUPLICATE_SERIAL_NUMBER_WARNING')}
              />
            </Grid>
          )}
          <Grid item xs={12} css={{ margin: theme.spacing(4, 0, 0, 0) }}>
            <Box justifyContent={'center'} display={'flex'}>
              <PGEButton
                data-testid={'pay-online-button'}
                onClick={jobQuoteForm.submit(async values => {
                  try {
                    const applicationNo = jobData?.applicationNo;
                    const serialNumber = values.modalIdNumber;
                    if (!applicationNo || !serialNumber) {
                      throw new Error(
                        'Missing application number or serial number',
                      );
                    }

                    if (
                      warnedInput?.applicationNo !== applicationNo ||
                      warnedInput?.serialNumber !== serialNumber
                    ) {
                      const isDuplicate = await validateSerialNumber({
                        serialNumber,
                      });
                      if (isDuplicate) {
                        setWarnedInput({ applicationNo, serialNumber });
                        return;
                      }
                    }

                    await submitJob(params?.jobId, applicationNo);
                  } catch (e) {
                    setErrorNotification(true);
                  }
                })}
                size={'large'}
                css={{ ...button }}
                disabled={isCalculatingRebates || isValidating}
              >
                {content.get('SUBMIT')}
              </PGEButton>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            css={{
              marginTop: 50,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            <Link
              to={routes.PGE_PLUS_INSTALLER_PORTAL_JOB_CANCEL.replace(
                ':jobId',
                params?.jobId,
              )}
            >
              {content.get('CANCEL_JOB_LINK_TEXT')}
            </Link>
          </Grid>
          <hr css={{ ...hr }} />
        </Grid>
      </Box>
    </Fragment>
  );
};

export default ConfirmDetailsAndCost;
